import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import './GuestModal.css';
import PhoneInput from './PhoneInput';

const EditGuestModal = ({ show, onHide, guest, onGuestUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredLanguage: 'ar',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if source allows editing
  const isEditableSource = () => {
    if (!guest) return false;
    const editableSources = ['direct', 'manual'];
    return editableSources.includes(guest.acquisitionSource);
  };

  const canEditEmail = () => {
    if (!guest) return false;
    // Only allow email editing for manual guests (no bookings yet)
    return guest.acquisitionSource === 'manual';
  };

  // Populate form when guest changes
  useEffect(() => {
    if (guest) {
      setFormData({
        name: guest.name || '',
        email: guest.email || '',
        phone: guest.phone || '',
        preferredLanguage: guest.preferredLanguage || 'ar',
        notes: guest.notes || ''
      });
    }
  }, [guest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('اسم الضيف مطلوب');
      return;
    }

    if (!isEditableSource()) {
      setError('لا يمكن تعديل بيانات ضيوف المنصات الخارجية');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Prepare update data (exclude email if not editable)
      const updateData = {
        name: formData.name,
        phone: formData.phone,
        preferredLanguage: formData.preferredLanguage,
        notes: formData.notes
      };

      // Only include email if source allows it
      if (canEditEmail()) {
        updateData.email = formData.email;
      }

      const response = await axios.put(
        `/api/guests/${guest._id}`,
        updateData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Notify parent and close
        if (onGuestUpdated) {
          onGuestUpdated(response.data.guest);
        }
        onHide();
      }
    } catch (err) {
      console.error('Error updating guest:', err);
      setError(
        err.response?.data?.message ||
        'حدث خطأ في تحديث بيانات الضيف'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onHide();
  };

  const getSourceLabel = (source) => {
    const labels = {
      direct: 'Host Me',
      airbnb: 'Airbnb',
      booking: 'Booking.com',
      expedia: 'Expedia',
      ical: 'iCal',
      gathern: 'Gathern',
      almosafer: 'المسافر',
      manual: 'يدوي',
      other: 'آخر'
    };
    return labels[source] || source;
  };

  if (!guest) return null;

  return (
    <Modal show={show} onHide={handleClose} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-pencil me-2"></i>
          تعديل بيانات الضيف
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isEditableSource() && (
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            لا يمكن تعديل بيانات ضيوف {getSourceLabel(guest.acquisitionSource)} (حماية الخصوصية)
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              الاسم <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="أدخل اسم الضيف"
              required
              disabled={loading || !isEditableSource()}
              className="touch-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>البريد الإلكتروني</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@email.com"
              disabled={loading || !canEditEmail()}
              className="touch-input"
            />
            {!canEditEmail() && guest.email && (
              <Form.Text className="text-muted">
                <i className="bi bi-lock me-1"></i>
                لا يمكن تعديل البريد الإلكتروني لضيوف المنصات الخارجية
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>رقم الهاتف</Form.Label>
            <PhoneInput
              value={formData.phone}
              defaultCountry="SA"
              onChange={(phone) => { setFormData(prev => ({ ...prev, phone })); setError(null); }}
              disabled={loading || !isEditableSource()}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>اللغة المفضلة</Form.Label>
            <Form.Select
              name="preferredLanguage"
              value={formData.preferredLanguage}
              onChange={handleChange}
              disabled={loading || !isEditableSource()}
              className="touch-select"
            >
              <option value="ar">العربية</option>
              <option value="en">English</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>ملاحظات</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="ملاحظات إضافية عن الضيف..."
              disabled={loading || !isEditableSource()}
              className="touch-input"
            />
          </Form.Group>

          {/* Guest Source Info */}
          <div className="guest-source-info">
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              المصدر: {getSourceLabel(guest.acquisitionSource)} |
              الحجوزات: {guest.totalBookings || 0}
            </small>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="touch-modal-actions">
        <Button variant="secondary" onClick={handleClose} disabled={loading} className="touch-btn">
          إلغاء
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || !formData.name.trim() || !isEditableSource()}
          className="touch-btn"
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              جاري الحفظ...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              حفظ التعديلات
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditGuestModal;
